import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getChiusure, createChiusura, updateChiusura, deleteChiusura, getSpeseGiornoChiusura, uploadScontrino, getFondoCassaPrecedente } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Trash2, Calculator, Lock, ChevronDown, ChevronUp, Receipt, User, Truck, Pencil, Eye, Upload, Image, X, ArrowDown, ArrowUp, Minus, ChevronRight, TrendingUp, Calendar } from 'lucide-react';
import { format, parseISO, startOfMonth, getMonth, getYear } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const CATEGORIE_COLORS = {
  personale: 'bg-blue-500',
  varie: 'bg-purple-500',
  fornitori: 'bg-green-500',
  generiche: 'bg-orange-500',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Costruisce l'URL dello scontrino aggiungendo il token JWT come query param
// (necessario per le immagini servite da cloud storage che richiedono auth)
const buildScontrinoUrl = (url) => {
  if (!url) return '';
  const fullUrl = `${API_BASE}${url}`;
  if (url.startsWith('/api/files/')) {
    const token = localStorage.getItem('token');
    return token ? `${fullUrl}?auth=${encodeURIComponent(token)}` : fullUrl;
  }
  return fullUrl;
};

const Chiusure = () => {
  const [chiusure, setChiusure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [speseGiorno, setSpeseGiorno] = useState([]);
  const [loadingSpese, setLoadingSpese] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Edit/View mode
  const [editMode, setEditMode] = useState(null); // null | 'create' | 'edit'
  const [selectedChiusura, setSelectedChiusura] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewChiusura, setViewChiusura] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Fondo cassa precedente
  const [fondoPrecedente, setFondoPrecedente] = useState(null);
  const [loadingFondo, setLoadingFondo] = useState(false);

  // Mesi espansi per accordion
  const [expandedMonths, setExpandedMonths] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    data: format(new Date(), 'yyyy-MM-dd'),
    totale_fiscale: '',
    fondo_cassa: '',
    contanti_fiscali: '',
    contanti_in_cassa: '',
    note: ''
  });
  const [formFile, setFormFile] = useState(null);
  const [formFilePreview, setFormFilePreview] = useState(null);

  // Calcola totale spese giornaliere
  const speseGiornaliere = speseGiorno.reduce((acc, s) => acc + s.importo, 0);

  // Calcola variazione fondo cassa (Fondo Oggi - Fondo Ieri)
  // Negativo = preso dal fondo, Positivo = aggiunto al fondo
  const variazioneFondo = fondoPrecedente !== null 
    ? (parseFloat(formData.fondo_cassa) || 0) - fondoPrecedente
    : 0;

  // Campi calcolati - Formula: Contanti Extra = Contanti in Cassa + Spese + Variazione Fondo - Contanti Fiscali
  const contantiExtra = (parseFloat(formData.contanti_in_cassa) || 0) + 
                        speseGiornaliere + 
                        variazioneFondo -
                        (parseFloat(formData.contanti_fiscali) || 0);
  
  const totaleCompleto = (parseFloat(formData.totale_fiscale) || 0) + contantiExtra;

  // Raggruppa chiusure per mese
  const chiusurePerMese = chiusure.reduce((acc, chiusura) => {
    const data = parseISO(chiusura.data);
    const meseKey = format(data, 'yyyy-MM');
    const meseLabel = format(data, 'MMMM yyyy', { locale: it });
    
    if (!acc[meseKey]) {
      acc[meseKey] = {
        meseKey,
        meseLabel,
        chiusure: [],
        totaleFiscale: 0,
        totaleExtra: 0,
        totaleCompleto: 0,
        totaleSpese: 0,
        giorniRegistrati: 0
      };
    }
    
    acc[meseKey].chiusure.push(chiusura);
    acc[meseKey].totaleFiscale += chiusura.totale_fiscale || 0;
    acc[meseKey].totaleExtra += chiusura.contanti_extra || 0;
    acc[meseKey].totaleCompleto += chiusura.totale_completo || 0;
    acc[meseKey].totaleSpese += chiusura.spese_giornaliere || 0;
    acc[meseKey].giorniRegistrati++;
    
    return acc;
  }, {});

  // Converti in array ordinato per mese (più recente prima)
  const mesiOrdinati = Object.values(chiusurePerMese).sort((a, b) => b.meseKey.localeCompare(a.meseKey));

  // Espandi automaticamente il mese corrente al primo caricamento
  useEffect(() => {
    if (mesiOrdinati.length > 0 && Object.keys(expandedMonths).length === 0) {
      setExpandedMonths({ [mesiOrdinati[0].meseKey]: true });
    }
  }, [mesiOrdinati.length]);

  const toggleMonth = (meseKey) => {
    setExpandedMonths(prev => ({ ...prev, [meseKey]: !prev[meseKey] }));
  };

  // Carica spese quando cambia la data (TUTTE le spese del giorno per la chiusura)
  const loadSpeseGiorno = useCallback(async (data) => {
    setLoadingSpese(true);
    try {
      const spese = await getSpeseGiornoChiusura(data);
      setSpeseGiorno(spese);
    } catch (error) {
      console.error('Errore caricamento spese:', error);
      setSpeseGiorno([]);
    } finally {
      setLoadingSpese(false);
    }
  }, []);

  // Carica fondo cassa precedente quando cambia la data
  const loadFondoPrecedente = useCallback(async (data) => {
    setLoadingFondo(true);
    try {
      const result = await getFondoCassaPrecedente(data);
      setFondoPrecedente(result.fondo_cassa_precedente);
    } catch (error) {
      console.error('Errore caricamento fondo precedente:', error);
      setFondoPrecedente(null);
    } finally {
      setLoadingFondo(false);
    }
  }, []);

  useEffect(() => {
    loadChiusure();
  }, []);

  useEffect(() => {
    if (showForm) {
      loadSpeseGiorno(formData.data);
      loadFondoPrecedente(formData.data);
    }
  }, [formData.data, showForm, loadSpeseGiorno, loadFondoPrecedente]);

  const loadChiusure = async () => {
    try {
      const data = await getChiusure();
      setChiusure(data);
    } catch (error) {
      toast.error('Errore nel caricamento chiusure');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      data: format(new Date(), 'yyyy-MM-dd'),
      totale_fiscale: '',
      fondo_cassa: '',
      contanti_fiscali: '',
      contanti_in_cassa: '',
      note: ''
    });
    setSpeseGiorno([]);
    setFondoPrecedente(null);
    setFormFile(null);
    setFormFilePreview(null);
    setEditMode(null);
    setSelectedChiusura(null);
  };

  const handleFormFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFormFile = () => {
    setFormFile(null);
    setFormFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.totale_fiscale) {
      toast.error('Inserisci il totale fiscale');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        data: formData.data,
        totale_fiscale: parseFloat(formData.totale_fiscale) || 0,
        fondo_cassa: parseFloat(formData.fondo_cassa) || 0,
        contanti_fiscali: parseFloat(formData.contanti_fiscali) || 0,
        contanti_in_cassa: parseFloat(formData.contanti_in_cassa) || 0,
        spese_giornaliere: speseGiornaliere,
        note: formData.note || null
      };

      let createdId = null;
      if (editMode === 'edit' && selectedChiusura) {
        await updateChiusura(selectedChiusura.id, payload);
        toast.success('Chiusura aggiornata!');
      } else {
        const result = await createChiusura(payload);
        createdId = result.id;
        toast.success('Chiusura registrata!');
      }
      
      // Upload foto se presente (solo per nuova creazione)
      if (formFile && createdId) {
        try {
          await uploadScontrino(createdId, formFile);
          toast.success('Scontrino caricato!');
        } catch (uploadError) {
          toast.error('Chiusura salvata, ma errore nel caricamento scontrino');
        }
      }
      
      resetForm();
      setShowForm(false);
      loadChiusure();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (chiusura) => {
    setSelectedChiusura(chiusura);
    setEditMode('edit');
    setFormData({
      data: chiusura.data,
      totale_fiscale: chiusura.totale_fiscale?.toString() || '',
      fondo_cassa: chiusura.fondo_cassa?.toString() || '',
      contanti_fiscali: chiusura.contanti_fiscali?.toString() || '',
      contanti_in_cassa: chiusura.contanti_in_cassa?.toString() || '',
      note: chiusura.note || ''
    });
    setShowForm(true);
  };

  const handleView = (chiusura) => {
    setViewChiusura(chiusura);
    setViewDialog(true);
  };

  const handleDelete = async (id, chiusura) => {
    if (!isAdmin && !chiusura.is_owner) {
      toast.error('Non puoi eliminare le chiusure di altri');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questa chiusura?')) return;
    try {
      await deleteChiusura(id);
      toast.success('Chiusura eliminata!');
      loadChiusure();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUploadScontrino = async (chiusuraId, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadScontrino(chiusuraId, file);
      toast.success('Scontrino caricato!');
      loadChiusure();
      // Aggiorna anche la vista se aperta
      if (viewChiusura && viewChiusura.id === chiusuraId) {
        setViewChiusura({ ...viewChiusura, scontrino_url: result.scontrino_url });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const getSpesaDetail = (spesa) => {
    if (spesa.categoria === 'personale' && spesa.persona) {
      return spesa.persona;
    }
    if (spesa.categoria === 'fornitori' && spesa.fornitore_nome) {
      return spesa.fornitore_nome;
    }
    return null;
  };

  // Calcola totali per admin
  const totaleFiscaleTotale = chiusure.reduce((acc, c) => acc + (c.totale_fiscale || c.incasso_totale || 0), 0);
  const totaleCompletoTotale = chiusure.reduce((acc, c) => acc + (c.totale_completo || c.incasso_totale || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="chiusure-page">
      <div>
        <h1 className="text-3xl font-bold">Chiusure Fiscali</h1>
        <p className="text-muted-foreground mt-1">Registra gli incassi giornalieri</p>
      </div>

      {/* Form Card */}
      <Card className="border-primary/30">
        <CardHeader 
          className="cursor-pointer flex flex-row items-center justify-between py-4"
          onClick={() => {
            if (!showForm) {
              resetForm();
              setEditMode('create');
            }
            setShowForm(!showForm);
          }}
        >
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            {editMode === 'edit' ? 'Modifica Chiusura' : 'Nuova Chiusura'}
          </CardTitle>
          <Button variant="ghost" size="icon">
            {showForm ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </Button>
        </CardHeader>
        
        {showForm && (
          <CardContent className="pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Data */}
              <div className="space-y-2">
                <Label htmlFor="data">Data Chiusura</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  className="h-12"
                  data-testid="chiusura-data"
                />
              </div>

              {/* Spese Giornaliere - Automatiche */}
              <div className="p-4 bg-secondary/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Spese Giornaliere
                  </span>
                  {loadingSpese ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  ) : (
                    <span className="text-lg font-bold font-mono text-destructive">
                      €{speseGiornaliere.toFixed(2)}
                    </span>
                  )}
                </div>
                
                {speseGiorno.length > 0 ? (
                  <div className="space-y-2 mt-2">
                    {speseGiorno.map((spesa, idx) => {
                      const detail = getSpesaDetail(spesa);
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm p-2 bg-background/50 rounded">
                          <div className="flex items-center gap-2">
                            <Badge className={`${CATEGORIE_COLORS[spesa.categoria]} text-white text-xs`}>
                              {spesa.categoria}
                            </Badge>
                            <span>{spesa.descrizione}</span>
                            {detail && (
                              <span className="text-muted-foreground">
                                {spesa.categoria === 'personale' ? (
                                  <span className="flex items-center gap-1"><User className="h-3 w-3" />{detail}</span>
                                ) : (
                                  <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{detail}</span>
                                )}
                              </span>
                            )}
                          </div>
                          <span className="font-mono font-medium">€{spesa.importo.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Nessuna spesa registrata per questo giorno
                  </p>
                )}
              </div>

              {/* Totale Fiscale */}
              <div className="space-y-2">
                <Label htmlFor="totale_fiscale">Totale Fiscale € *</Label>
                <Input
                  id="totale_fiscale"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={formData.totale_fiscale}
                  onChange={(e) => setFormData({ ...formData, totale_fiscale: e.target.value })}
                  placeholder="0.00"
                  className="h-12 text-lg"
                  data-testid="chiusura-totale-fiscale"
                />
              </div>

              {/* Fondo Cassa */}
              <div className="space-y-2">
                <Label htmlFor="fondo_cassa">Fondo Cassa €</Label>
                <Input
                  id="fondo_cassa"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={formData.fondo_cassa}
                  onChange={(e) => setFormData({ ...formData, fondo_cassa: e.target.value })}
                  placeholder="0.00"
                  className="h-12 text-lg"
                  data-testid="chiusura-fondo-cassa"
                />
                
                {/* Confronto fondo cassa precedente */}
                {fondoPrecedente !== null && (
                  <div className={`p-3 rounded-lg ${variazioneFondo === 0 ? 'bg-secondary/50' : variazioneFondo < 0 ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Fondo precedente: <span className="font-mono font-medium">€{fondoPrecedente.toFixed(2)}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        {variazioneFondo < 0 ? (
                          <>
                            <ArrowDown className="h-4 w-4 text-orange-500" />
                            <span className="font-mono font-medium text-orange-500">
                              €{Math.abs(variazioneFondo).toFixed(2)} preso dal fondo
                            </span>
                          </>
                        ) : variazioneFondo > 0 ? (
                          <>
                            <ArrowUp className="h-4 w-4 text-green-500" />
                            <span className="font-mono font-medium text-green-500">
                              €{variazioneFondo.toFixed(2)} rimesso nel fondo
                            </span>
                          </>
                        ) : (
                          <>
                            <Minus className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-muted-foreground">invariato</span>
                          </>
                        )}
                      </div>
                    </div>
                    {variazioneFondo !== 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {variazioneFondo < 0 
                          ? 'I soldi presi dal fondo vengono sottratti dai contanti extra' 
                          : 'I soldi rimessi nel fondo vengono aggiunti ai contanti extra'}
                      </p>
                    )}
                  </div>
                )}
                {loadingFondo && (
                  <div className="text-sm text-muted-foreground">Caricamento fondo precedente...</div>
                )}
              </div>

              {/* Contanti Fiscali */}
              <div className="space-y-2">
                <Label htmlFor="contanti_fiscali">Contanti Fiscali €</Label>
                <Input
                  id="contanti_fiscali"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={formData.contanti_fiscali}
                  onChange={(e) => setFormData({ ...formData, contanti_fiscali: e.target.value })}
                  placeholder="0.00"
                  className="h-12 text-lg"
                  data-testid="chiusura-contanti-fiscali"
                />
              </div>

              {/* Contanti in Cassa */}
              <div className="space-y-2">
                <Label htmlFor="contanti_in_cassa">Contanti in Cassa €</Label>
                <Input
                  id="contanti_in_cassa"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={formData.contanti_in_cassa}
                  onChange={(e) => setFormData({ ...formData, contanti_in_cassa: e.target.value })}
                  placeholder="0.00"
                  className="h-12 text-lg"
                  data-testid="chiusura-contanti-cassa"
                />
              </div>

              {/* Campi Calcolati */}
              <div className="p-4 bg-secondary/50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Contanti Extra</span>
                  <span className={`font-mono font-semibold ${contantiExtra >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    €{contantiExtra.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  = Contanti in cassa + Spese ({speseGiornaliere.toFixed(2)})
                  {variazioneFondo !== 0 && (
                    <span className={variazioneFondo < 0 ? 'text-orange-500' : 'text-green-500'}>
                      {variazioneFondo < 0 ? ` - Preso fondo (${Math.abs(variazioneFondo).toFixed(2)})` : ` + Rimesso fondo (${variazioneFondo.toFixed(2)})`}
                    </span>
                  )}
                  {' '}- Contanti fiscali
                </p>
              </div>

              {/* Totale Completo */}
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-primary font-semibold">Totale Completo</span>
                  <span className="text-2xl font-bold font-mono text-primary">
                    €{totaleCompleto.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  = Totale fiscale + Contanti extra
                </p>
              </div>

              {/* Note */}
              <div className="space-y-2">
                <Label htmlFor="note">Note (opzionale)</Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  rows={2}
                  data-testid="chiusura-note"
                />
              </div>

              {/* Upload foto scontrino - solo in creazione */}
              {editMode === 'create' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Foto Scontrino (opzionale)
                  </Label>
                  
                  {formFilePreview ? (
                    <div className="relative">
                      <img 
                        src={formFilePreview}
                        alt="Anteprima scontrino"
                        className="w-full max-h-48 object-contain rounded-lg border bg-secondary/30"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={removeFormFile}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFormFileChange}
                        data-testid="chiusura-file-input"
                      />
                      <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Clicca per scattare o selezionare foto
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1 h-12"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 h-12"
                  disabled={saving}
                  data-testid="chiusura-submit"
                >
                  {saving ? 'Salvataggio...' : editMode === 'edit' ? 'Aggiorna' : 'Registra'}
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Totali - solo per admin */}
      {isAdmin && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Totale Fiscale</p>
              <p className="text-2xl font-bold font-mono text-blue-500">
                €{totaleFiscaleTotale.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Totale Completo</p>
              <p className="text-2xl font-bold font-mono text-primary">
                €{totaleCompletoTotale.toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista chiusure per mese */}
      {chiusure.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calculator className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessuna chiusura registrata</p>
            <p className="text-sm text-muted-foreground">Apri il form sopra per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {mesiOrdinati.map((mese) => (
            <Collapsible 
              key={mese.meseKey} 
              open={expandedMonths[mese.meseKey]} 
              onOpenChange={() => toggleMonth(mese.meseKey)}
            >
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-primary" />
                        <div>
                          <CardTitle className="text-lg capitalize">{mese.meseLabel}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {mese.giorniRegistrati} giorn{mese.giorniRegistrati === 1 ? 'o' : 'i'} registrat{mese.giorniRegistrati === 1 ? 'o' : 'i'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {/* Riepilogo mensile inline */}
                        <div className="hidden md:flex items-center gap-6 text-right">
                          <div>
                            <p className="text-xs text-muted-foreground">Fiscale</p>
                            <p className="font-mono font-semibold text-blue-500">€{mese.totaleFiscale.toFixed(0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Extra</p>
                            <p className={`font-mono font-semibold ${mese.totaleExtra >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              €{mese.totaleExtra.toFixed(0)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Completo</p>
                            <p className="font-mono font-bold text-primary">€{mese.totaleCompleto.toFixed(0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Spese</p>
                            <p className="font-mono text-orange-500">€{mese.totaleSpese.toFixed(0)}</p>
                          </div>
                        </div>
                        
                        {expandedMonths[mese.meseKey] ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {/* Riepilogo mobile */}
                    <div className="md:hidden grid grid-cols-4 gap-2 mt-3 pt-3 border-t">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Fiscale</p>
                        <p className="font-mono text-sm font-semibold text-blue-500">€{mese.totaleFiscale.toFixed(0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Extra</p>
                        <p className={`font-mono text-sm font-semibold ${mese.totaleExtra >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          €{mese.totaleExtra.toFixed(0)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Completo</p>
                        <p className="font-mono text-sm font-bold text-primary">€{mese.totaleCompleto.toFixed(0)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">Spese</p>
                        <p className="font-mono text-sm text-orange-500">€{mese.totaleSpese.toFixed(0)}</p>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    {/* Riepilogo dettagliato */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-secondary/30 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Media giornaliera</p>
                        <p className="font-mono font-semibold">€{(mese.totaleCompleto / mese.giorniRegistrati).toFixed(0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Media Extra/giorno</p>
                        <p className={`font-mono font-semibold ${mese.totaleExtra >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          €{(mese.totaleExtra / mese.giorniRegistrati).toFixed(0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Media Spese/giorno</p>
                        <p className="font-mono text-orange-500">€{(mese.totaleSpese / mese.giorniRegistrati).toFixed(0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Totale Spese mese</p>
                        <p className="font-mono text-orange-500">€{mese.totaleSpese.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    {/* Tabella chiusure del mese */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Data</TableHead>
                            <TableHead className="text-right">Fiscale</TableHead>
                            <TableHead className="text-right">Extra</TableHead>
                            <TableHead className="text-right">Completo</TableHead>
                            {isAdmin && <TableHead>Registrata da</TableHead>}
                            <TableHead className="text-center">Scontrino</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mese.chiusure.map((chiusura) => {
                            const canEdit = isAdmin || chiusura.is_owner;
                            return (
                              <TableRow key={chiusura.id} data-testid={`chiusura-row-${chiusura.id}`}>
                                <TableCell className="font-mono text-sm">
                                  {format(parseISO(chiusura.data), 'dd/MM/yy')}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  €{(chiusura.totale_fiscale || 0).toFixed(0)}
                                </TableCell>
                                <TableCell className={`text-right font-mono ${(chiusura.contanti_extra || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  €{(chiusura.contanti_extra || 0).toFixed(0)}
                                </TableCell>
                                <TableCell className="text-right font-mono font-bold text-primary">
                                  €{(chiusura.totale_completo || 0).toFixed(0)}
                                </TableCell>
                                {isAdmin && (
                                  <TableCell className="text-sm text-muted-foreground">
                                    {chiusura.user_nome || '-'}
                                  </TableCell>
                                )}
                                <TableCell className="text-center">
                                  {chiusura.scontrino_url ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-green-500"
                                      onClick={() => handleView(chiusura)}
                                      title="Visualizza scontrino"
                                    >
                                      <Image className="h-4 w-4" />
                                    </Button>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleView(chiusura)}
                                      title="Visualizza dettagli"
                                      data-testid={`view-chiusura-${chiusura.id}`}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    
                                    {canEdit && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEdit(chiusura)}
                                        title="Modifica"
                                        data-testid={`edit-chiusura-${chiusura.id}`}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    )}
                                    
                                    {canEdit ? (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => handleDelete(chiusura.id, chiusura)}
                                        data-testid={`delete-chiusura-${chiusura.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <Lock className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Dettaglio Chiusura - {viewChiusura && format(new Date(viewChiusura.data), 'dd/MM/yyyy')}
            </DialogTitle>
            <DialogDescription>
              Visualizza tutti i dettagli della chiusura fiscale e carica lo scontrino
            </DialogDescription>
          </DialogHeader>
          
          {viewChiusura && (
            <div className="space-y-4">
              {/* Dettagli numerici */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Totale Fiscale</p>
                  <p className="text-xl font-bold font-mono">€{(viewChiusura.totale_fiscale || 0).toFixed(2)}</p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Fondo Cassa</p>
                  <p className="text-xl font-bold font-mono">€{(viewChiusura.fondo_cassa || 0).toFixed(2)}</p>
                  {viewChiusura.fondo_cassa_precedente !== null && viewChiusura.fondo_cassa_precedente !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      (prec: €{viewChiusura.fondo_cassa_precedente.toFixed(2)})
                    </p>
                  )}
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Contanti Fiscali</p>
                  <p className="text-xl font-bold font-mono">€{(viewChiusura.contanti_fiscali || 0).toFixed(2)}</p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Contanti in Cassa</p>
                  <p className="text-xl font-bold font-mono">€{(viewChiusura.contanti_in_cassa || 0).toFixed(2)}</p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Spese Giornaliere</p>
                  <p className="text-xl font-bold font-mono text-destructive">€{(viewChiusura.spese_giornaliere || 0).toFixed(2)}</p>
                </div>
                {(viewChiusura.variazione_fondo !== null && viewChiusura.variazione_fondo !== undefined && viewChiusura.variazione_fondo !== 0) && (
                  <div className={`p-3 rounded-lg ${viewChiusura.variazione_fondo < 0 ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
                    <p className="text-xs text-muted-foreground">Variazione Fondo</p>
                    <p className={`text-xl font-bold font-mono ${viewChiusura.variazione_fondo < 0 ? 'text-orange-500' : 'text-green-500'}`}>
                      {viewChiusura.variazione_fondo < 0 ? '-' : '+'}€{Math.abs(viewChiusura.variazione_fondo).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {viewChiusura.variazione_fondo < 0 ? 'preso dal fondo' : 'rimesso nel fondo'}
                    </p>
                  </div>
                )}
              </div>

              {/* Contanti Extra */}
              <div className={`p-3 rounded-lg ${(viewChiusura.contanti_extra || 0) >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Contanti Extra</span>
                  <span className={`text-xl font-bold font-mono ${(viewChiusura.contanti_extra || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    €{(viewChiusura.contanti_extra || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Totale Completo */}
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-primary font-semibold">Totale Completo</span>
                  <span className="text-2xl font-bold font-mono text-primary">
                    €{(viewChiusura.totale_completo || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Note */}
              {viewChiusura.note && (
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Note</p>
                  <p className="text-sm">{viewChiusura.note}</p>
                </div>
              )}

              {/* Registrata da */}
              {viewChiusura.user_nome && (
                <div className="text-sm text-muted-foreground">
                  Registrata da: <span className="font-medium">{viewChiusura.user_nome}</span>
                </div>
              )}

              {/* Scontrino */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Foto Scontrino
                </Label>
                
                {viewChiusura.scontrino_url ? (
                  <div className="relative">
                    <img 
                      src={buildScontrinoUrl(viewChiusura.scontrino_url)}
                      alt="Scontrino chiusura"
                      className="w-full rounded-lg border"
                    />
                    <a 
                      href={buildScontrinoUrl(viewChiusura.scontrino_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                    <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Nessuno scontrino caricato</p>
                    
                    {(isAdmin || viewChiusura.is_owner) && (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleUploadScontrino(viewChiusura.id, e.target.files[0]);
                            }
                          }}
                          disabled={uploading}
                        />
                        <Button variant="outline" size="sm" disabled={uploading} asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading ? 'Caricamento...' : 'Carica Scontrino'}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                )}

                {/* Upload button if already has image */}
                {viewChiusura.scontrino_url && (isAdmin || viewChiusura.is_owner) && (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleUploadScontrino(viewChiusura.id, e.target.files[0]);
                        }
                      }}
                      disabled={uploading}
                    />
                    <Button variant="outline" size="sm" disabled={uploading} className="w-full" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Caricamento...' : 'Sostituisci Scontrino'}
                      </span>
                    </Button>
                  </label>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chiusure;
